import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";

// redux toolkit
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { fetchPackage, updatePackage } from "../../slices/apps/packageSlice";

// router
import { useLocation, useNavigate } from "react-router-dom";

// Formik validation
import * as Yup from "yup";
import { useFormik } from "formik";

// diğer importlar
import { NumericFormat } from "react-number-format";

// quill
import { useQuill } from "react-quilljs";
import "quill/dist/quill.snow.css";
import "./quill-styles.css";

// --- YARDIMCI FONKSİYONLAR ---

const createDescSchema = (limitFieldName) => {
  return Yup.string().when(limitFieldName, {
    is: (val) => {
      return val !== undefined && val !== null && Number(val) > 0;
    },
    then: (schema) => schema.required("Lütfen açıklama girin"),
    otherwise: (schema) => schema.notRequired(),
  });
};

const limitSchema = Yup.number()
  .transform((value, originalValue) => {
    return originalValue === "" ? undefined : value;
  })
  .required("Limit girmelisiniz")
  .typeError("Sayısal değer giriniz")
  .min(0, "Negatif olamaz");

// --- ALT BİLEŞEN (Input Grubu) ---
const LimitInputItem = ({
  label,
  limitName,
  descName,
  formik,
  isCurrency = false,
}) => {
  const isLimitError = formik.touched[limitName] && formik.errors[limitName];
  const isDescError = formik.touched[descName] && formik.errors[descName];

  return (
    <Col xs={12} md={6}>
      <Label className="form-label">{label}</Label>

      <div className="d-flex flex-column gap-2">
        <div>
          <NumericFormat
            name={limitName}
            placeholder={isCurrency ? "0,00" : "0"}
            className={`form-control ${isLimitError ? "is-invalid" : ""}`}
            value={formik.values[limitName]}
            decimalSeparator=","
            thousandSeparator="."
            decimalScale={isCurrency ? 2 : 0}
            fixedDecimalScale={isCurrency}
            allowNegative={false}
            suffix={isCurrency ? " ₺" : ""}
            onValueChange={(values) => {
              const val =
                values.floatValue === undefined ? 0 : values.floatValue;
              formik.setFieldValue(limitName, val);
            }}
          />
        </div>

        <div>
          <Input
            type="text"
            placeholder="Açıklama"
            name={descName}
            className={`form-control ${isDescError ? "is-invalid" : ""}`}
            value={formik.values[descName] || ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </div>
      </div>

      {(isLimitError || isDescError) && (
        <div className="invalid-feedback d-block mt-1">
          {isLimitError && (
            <div>
              <i className="ri-error-warning-line me-1"></i>
              {formik.errors[limitName]}
            </div>
          )}
          {isDescError && (
            <div>
              <i className="ri-error-warning-line me-1"></i>
              {formik.errors[descName]}
            </div>
          )}
        </div>
      )}
    </Col>
  );
};

const PackageUpdateNew = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { state } = location;

  const [selectedImage, setSelectedImage] = useState(null);

  // Quill ayarları
  const quillModules = {
    toolbar: [
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ color: [] }],
      ["clean"],
    ],
  };

  const { quillRef: descriptionQuillRef, quill: descriptionQuill } = useQuill({
    modules: quillModules,
  });
  const { quillRef: agreementQuillRef, quill: agreementQuill } = useQuill({
    modules: quillModules,
  });

  // Redux state
  const { packageState, packageLoading } = useSelector((state) => {
    return {
      packageState: state.package.package,
      packageLoading: state.package.loading,
    };
  }, shallowEqual);

  // Veriyi çek
  const fetchData = async () => {
    try {
      if (state?.id) {
        await dispatch(fetchPackage(state.id));
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    if (!(state && state.id)) {
      navigate("/package-admin");
    } else {
      fetchData();
    }
  }, [state, dispatch]);

  // Formik Ayarları
  const packageValidation = useFormik({
    enableReinitialize: true,

    initialValues: {
      id: packageState?.Id || state?.id || 0,
      packageName: packageState?.PackageName || "",
      price: packageState?.Price || 0,

      // Resim
      picture: null,
      imageUrl: packageState?.ImageUrl || packageState?.PicturePath || "",

      // Mevcut Limitler
      totalLimit: packageState?.TotalLimit || 0,
      totalLimitDesc: packageState?.TotalLimitDesc || "",
      towingLimit: packageState?.TowingLimit || 0,
      towingLimitDesc: packageState?.TowingLimitDesc || "",
      windshield: packageState?.Windshield || 0,
      windshieldDesc: packageState?.WindshieldDesc || "",
      accident: packageState?.Accident || 0,
      accidentDesc: packageState?.AccidentDesc || "",
      fault: packageState?.Fault || 0,
      faultDesc: packageState?.FaultDesc || "",
      tire: packageState?.Tire || 0,
      tireDesc: packageState?.TireDesc || "",
      fuel: packageState?.Fuel || 0,
      fuelDesc: packageState?.FuelDesc || "",
      landslide: packageState?.Landslide || 0,
      landslideDesc: packageState?.LandslideDesc || "",
      headlight: packageState?.Headlight || 0,
      headlightDesc: packageState?.HeadlightDesc || "",
      health: packageState?.Health || 0,
      healthDesc: packageState?.HealthDesc || "",
      tired: packageState?.Tired || 0,
      tiredDesc: packageState?.TiredDesc || "",
      evFault: packageState?.EVFault || 0,
      evFaultDesc: packageState?.EVFaultDesc || "",
      wildAnimal: packageState?.WildAnimal || 0,
      wildAnimalDesc: packageState?.WildAnimalDesc || "",

      // -- YENİ EKLENEN ALANLAR (State Eşleştirmesi) --
      ambulance: packageState?.Ambulance || 0,
      ambulanceDesc: packageState?.AmbulanceDesc || "",
      fireDepartment: packageState?.FireDepartment || 0,
      fireDepartmentDesc: packageState?.FireDepartmentDesc || "",
      locksmith: packageState?.Locksmith || 0,
      locksmithDesc: packageState?.LocksmithDesc || "",
      glass: packageState?.Glass || 0,
      glassDesc: packageState?.GlassDesc || "",
      // ---------------------------------------------

      // Diğer
      carYearLimit: packageState?.CarYearLimit || 0,
      isCarYearLimitEnabled: packageState?.IsCarYearLimitEnabled || false,

      description: packageState?.Description || "",
      agreement: packageState?.Agreement || "",

      isActive: packageState?.IsActive ?? true,
      isBulkSale: packageState?.IsBulkSale || false,
      bulkSaleId: packageState?.BulkSaleId || 0,
    },

    validationSchema: Yup.object({
      packageName: Yup.string().required("Lütfen paket adını girin"),
      price: Yup.number()
        .transform((value, originalValue) =>
          originalValue === "" ? undefined : value
        )
        .required("Lütfen fiyat girin")
        .moreThan(0, "Fiyat giriniz"),

      picture: Yup.mixed()
        .nullable()
        .test(
          "picture-required",
          "Lütfen bir görsel seçin",
          function (value) {
            const { imageUrl } = this.parent;
            // Yeni görsel seçilmişse veya mevcut görsel varsa geçerli
            if (value || imageUrl) {
              return true;
            }
            return false;
          }
        ),

      totalLimit: Yup.number()
        .transform((value, originalValue) => {
          return originalValue === "" ? undefined : value;
        })
        .required("Limit girmelisiniz")
        .typeError("Sayısal değer giriniz")
        .moreThan(0, "Limit giriniz"),
      totalLimitDesc: createDescSchema("totalLimit"),

      // Mevcut Validasyonlar
      windshield: limitSchema,
      windshieldDesc: createDescSchema("windshield"),
      accident: limitSchema,
      accidentDesc: createDescSchema("accident"),
      fault: limitSchema,
      faultDesc: createDescSchema("fault"),
      tire: limitSchema,
      tireDesc: createDescSchema("tire"),
      fuel: limitSchema,
      fuelDesc: createDescSchema("fuel"),
      landslide: limitSchema,
      landslideDesc: createDescSchema("landslide"),
      headlight: limitSchema,
      headlightDesc: createDescSchema("headlight"),
      health: limitSchema,
      healthDesc: createDescSchema("health"),
      tired: limitSchema,
      tiredDesc: createDescSchema("tired"),
      evFault: limitSchema,
      evFaultDesc: createDescSchema("evFault"),
      wildAnimal: limitSchema,
      wildAnimalDesc: createDescSchema("wildAnimal"),

      // -- YENİ ALAN VALIDASYONLARI --
      ambulance: limitSchema,
      ambulanceDesc: createDescSchema("ambulance"),
      fireDepartment: limitSchema,
      fireDepartmentDesc: createDescSchema("fireDepartment"),
      locksmith: limitSchema,
      locksmithDesc: createDescSchema("locksmith"),
      glass: limitSchema,
      glassDesc: createDescSchema("glass"),
      // ------------------------------

      carYearLimit: Yup.number().test(
        "carYearLimit-required",
        "Lütfen araç yaşı sınırı girin",
        function (value) {
          const { isCarYearLimitEnabled } = this.parent;
          if (isCarYearLimitEnabled) {
            return value !== undefined && value !== null && value !== "";
          }
          return true;
        }
      ),

      description: Yup.string().required("Lütfen açıklama girin"),
      agreement: Yup.string().required("Lütfen sözleşme girin"),
    }),

    onSubmit: async (values) => {
      const formData = new FormData();

      formData.append("Id", values.id);
      formData.append("PackageName", values.packageName);
      formData.append("Price", values.price);

      // Mevcut Limitler
      formData.append("TotalLimit", values.totalLimit);
      formData.append("TotalLimitDesc", values.totalLimitDesc);
      formData.append("TowingLimit", values.towingLimit);
      formData.append("TowingLimitDesc", values.towingLimitDesc);
      formData.append("Windshield", values.windshield);
      formData.append("WindshieldDesc", values.windshieldDesc);
      formData.append("Accident", values.accident);
      formData.append("AccidentDesc", values.accidentDesc);
      formData.append("Fault", values.fault);
      formData.append("FaultDesc", values.faultDesc);
      formData.append("Tire", values.tire);
      formData.append("TireDesc", values.tireDesc);
      formData.append("Fuel", values.fuel);
      formData.append("FuelDesc", values.fuelDesc);
      formData.append("Landslide", values.landslide);
      formData.append("LandslideDesc", values.landslideDesc);
      formData.append("Headlight", values.headlight);
      formData.append("HeadlightDesc", values.headlightDesc);
      formData.append("Health", values.health);
      formData.append("HealthDesc", values.healthDesc);
      formData.append("Tired", values.tired);
      formData.append("TiredDesc", values.tiredDesc);
      formData.append("EVFault", values.evFault);
      formData.append("EVFaultDesc", values.evFaultDesc);
      formData.append("WildAnimal", values.wildAnimal);
      formData.append("WildAnimalDesc", values.wildAnimalDesc);

      // -- YENİ ALANLAR (FormData'ya Ekleme) --
      formData.append("Ambulance", values.ambulance);
      formData.append("AmbulanceDesc", values.ambulanceDesc);

      formData.append("FireDepartment", values.fireDepartment);
      formData.append("FireDepartmentDesc", values.fireDepartmentDesc);

      formData.append("Locksmith", values.locksmith);
      formData.append("LocksmithDesc", values.locksmithDesc);

      formData.append("Glass", values.glass);
      formData.append("GlassDesc", values.glassDesc);
      // ---------------------------------------

      formData.append("Description", values.description);
      formData.append("Agreement", values.agreement);

      formData.append("IsActive", values.isActive);
      formData.append("IsBulkSale", values.isBulkSale);
      formData.append("IsCarYearLimitEnabled", values.isCarYearLimitEnabled);

      formData.append("CarYearLimit", values.carYearLimit);
      formData.append("BulkSaleId", values.bulkSaleId || 0);

      formData.append("ImageUrl", values.imageUrl);

      if (values.picture && values.picture instanceof File) {
        formData.append("Image", values.picture);
      }

      const resultAction = await dispatch(updatePackage(formData));

      if (resultAction.meta.requestStatus === "fulfilled") {
        navigate("/package-admin");
      }
    },
  });

  useEffect(() => {
    if (descriptionQuill && packageState?.Description) {
      const currentContent = descriptionQuill.root.innerHTML;
      if (
        currentContent === "<p><br></p>" ||
        currentContent !== packageState.Description
      ) {
        const prevScroll = window.scrollY;
        descriptionQuill.clipboard.dangerouslyPasteHTML(
          packageState.Description
        );
        descriptionQuill.blur();
        window.scrollTo(0, prevScroll);
      }
    }
  }, [descriptionQuill, packageState?.Description]);

  useEffect(() => {
    if (descriptionQuill) {
      descriptionQuill.on("text-change", () => {
        packageValidation.setFieldValue(
          "description",
          descriptionQuill.root.innerHTML
        );
      });
    }
  }, [descriptionQuill]);

  useEffect(() => {
    if (agreementQuill && packageState?.Agreement) {
      const currentContent = agreementQuill.root.innerHTML;
      if (
        currentContent === "<p><br></p>" ||
        currentContent !== packageState.Agreement
      ) {
        const prevScroll = window.scrollY;
        agreementQuill.clipboard.dangerouslyPasteHTML(packageState.Agreement);
        agreementQuill.blur();
        window.scrollTo(0, prevScroll);
      }
    }
  }, [agreementQuill, packageState?.Agreement]);

  useEffect(() => {
    if (agreementQuill) {
      agreementQuill.on("text-change", () => {
        packageValidation.setFieldValue(
          "agreement",
          agreementQuill.root.innerHTML
        );
      });
    }
  }, [agreementQuill]);

  document.title = "Paket Düzenle | Global Yol Yardım";

  const isDataNotReady =
    packageLoading ||
    Object.keys(packageState).length <= 0 ||
    (state?.id && packageState?.Id !== state.id);

  if (isDataNotReady) {
    return (
      <div
        className="page-content d-flex justify-content-center align-items-center"
        style={{ minHeight: "60vh" }}
      >
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              packageValidation.handleSubmit();
              return false;
            }}
            autoComplete="off"
          >
            <Row className="mb-3 align-items-center">
              <Col>
                <h4 className="card-title mb-0">Paket Düzenle</h4>
              </Col>
              <Col className="text-end">
                <Button color="primary" type="submit" className="btn-sm">
                  Güncelle
                </Button>
              </Col>
            </Row>

            <Row>
              <Col xl={4}>
                <Card>
                  <CardHeader>
                    <h5 className="card-title mb-0">Genel Bilgiler</h5>
                  </CardHeader>
                  <CardBody>
                    <div className="mb-3">
                      <Label htmlFor="packageName" className="form-label">
                        Paket Adı
                      </Label>
                      <Input
                        name="packageName"
                        className="form-control"
                        placeholder="Paket Adı"
                        value={packageValidation.values.packageName}
                        onChange={packageValidation.handleChange}
                        onBlur={packageValidation.handleBlur}
                        invalid={
                          !!(
                            packageValidation.touched.packageName &&
                            packageValidation.errors.packageName
                          )
                        }
                      />
                      <FormFeedback>
                        {packageValidation.errors.packageName}
                      </FormFeedback>
                    </div>

                    <div className="mb-3">
                      <Label htmlFor="price" className="form-label">
                        Fiyat
                      </Label>
                      <NumericFormat
                        name="price"
                        id="price"
                        placeholder="0,00"
                        className={`form-control ${
                          packageValidation.touched.price &&
                          packageValidation.errors.price
                            ? "is-invalid"
                            : ""
                        }`}
                        value={packageValidation.values.price}
                        decimalSeparator=","
                        thousandSeparator="."
                        decimalScale={2}
                        fixedDecimalScale={true}
                        suffix=" ₺"
                        allowNegative={false}
                        onValueChange={(vals) => {
                          const val =
                            vals.floatValue === undefined ? 0 : vals.floatValue;
                          packageValidation.setFieldValue("price", val);
                        }}
                      />
                      <div className="invalid-feedback d-block">
                        {packageValidation.touched.price &&
                          packageValidation.errors.price}
                      </div>
                    </div>

                    <div className="mb-3">
                      <Label className="form-label">Paket Görseli</Label>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.currentTarget.files[0];
                          if (file) {
                            packageValidation.setFieldValue("picture", file);
                            setSelectedImage(URL.createObjectURL(file));
                          }
                        }}
                        invalid={
                          !!(
                            packageValidation.touched.picture &&
                            packageValidation.errors.picture
                          )
                        }
                      />
                      <FormFeedback>
                        {packageValidation.errors.picture}
                      </FormFeedback>

                      {/* Resim Önizleme */}
                      {(selectedImage || packageValidation.values.imageUrl) && (
                        <div className="mt-3 text-center border rounded p-2 bg-light">
                          <p className="text-muted mb-1 fs-12">
                            {selectedImage
                              ? "Yeni Seçilen Görsel"
                              : "Mevcut Görsel"}
                          </p>
                          <img
                            src={
                              selectedImage || packageValidation.values.imageUrl
                            }
                            alt="Paket Görseli"
                            className="img-fluid"
                            style={{ maxHeight: "150px", objectFit: "contain" }}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        </div>
                      )}
                    </div>

                    <hr />

                    <div className="mb-3">
                      {/* Aktiflik Durumu */}
                      <div className="form-check form-switch form-switch-md mb-3">
                        <Input
                          type="checkbox"
                          role="switch"
                          id="isActive"
                          name="isActive"
                          checked={packageValidation.values.isActive}
                          onChange={packageValidation.handleChange}
                        />
                        <Label
                          htmlFor="isActive"
                          className="form-check-label ms-2"
                        >
                          Paket Aktif
                        </Label>
                      </div>

                      <div className="form-check form-switch form-switch-md mb-3">
                        <Input
                          type="checkbox"
                          role="switch"
                          id="isBulkSale"
                          name="isBulkSale"
                          checked={packageValidation.values.isBulkSale}
                          onChange={packageValidation.handleChange}
                        />
                        <Label
                          htmlFor="isBulkSale"
                          className="form-check-label ms-2"
                        >
                          Toplu Satış Paketi
                        </Label>
                      </div>

                      <div className="form-check form-switch form-switch-md">
                        <Input
                          type="checkbox"
                          role="switch"
                          id="isCarYearLimitEnabled"
                          name="isCarYearLimitEnabled"
                          checked={
                            packageValidation.values.isCarYearLimitEnabled
                          }
                          onChange={packageValidation.handleChange}
                        />
                        <Label
                          htmlFor="isCarYearLimitEnabled"
                          className="form-check-label ms-2"
                        >
                          Araç Yaş Sınırı Uygula
                        </Label>
                      </div>
                    </div>

                    {packageValidation.values.isCarYearLimitEnabled && (
                      <div className="mb-3">
                        <Label>Maksimum Araç Yaşı</Label>
                        <NumericFormat
                          name="carYearLimit"
                          placeholder="Yaş sınırı"
                          className={`form-control ${
                            packageValidation.touched.carYearLimit &&
                            packageValidation.errors.carYearLimit
                              ? "is-invalid"
                              : ""
                          }`}
                          value={packageValidation.values.carYearLimit}
                          allowNegative={false}
                          decimalScale={0}
                          onValueChange={(vals) => {
                            const val =
                              vals.floatValue === undefined
                                ? ""
                                : vals.floatValue;
                            packageValidation.setFieldValue(
                              "carYearLimit",
                              val
                            );
                          }}
                        />
                        <div className="invalid-feedback d-block">
                          {packageValidation.errors.carYearLimit}
                        </div>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>

              <Col xl={8}>
                <Card>
                  <CardHeader>
                    <h5 className="card-title mb-0">
                      Teminat Limitleri ve Açıklamaları
                    </h5>
                  </CardHeader>
                  <CardBody>
                    <Row className="g-4">
                      <LimitInputItem
                        label="Toplam Yıllık Limit"
                        limitName="totalLimit"
                        descName="totalLimitDesc"
                        formik={packageValidation}
                        isCurrency={true}
                      />

                      <LimitInputItem
                        label="Kaza Durumu Çekici"
                        limitName="accident"
                        descName="accidentDesc"
                        formik={packageValidation}
                      />

                      <LimitInputItem
                        label="Arıza Durumu Çekici"
                        limitName="fault"
                        descName="faultDesc"
                        formik={packageValidation}
                      />

                      <LimitInputItem
                        label="Lastik Patlaması"
                        limitName="tire"
                        descName="tireDesc"
                        formik={packageValidation}
                      />

                      <LimitInputItem
                        label="Yakıt Bitmesi"
                        limitName="fuel"
                        descName="fuelDesc"
                        formik={packageValidation}
                      />

                      <LimitInputItem
                        label="Ön Cam Kırılması"
                        limitName="windshield"
                        descName="windshieldDesc"
                        formik={packageValidation}
                      />

                      {/* --- YENİ EKLENEN ALANLAR (Inputlar) --- */}
                      <LimitInputItem
                        label="Cam Kırılması"
                        limitName="glass"
                        descName="glassDesc"
                        formik={packageValidation}
                      />

                      <LimitInputItem
                        label="Ambulans"
                        limitName="ambulance"
                        descName="ambulanceDesc"
                        formik={packageValidation}
                      />

                      <LimitInputItem
                        label="İtfaiye"
                        limitName="fireDepartment"
                        descName="fireDepartmentDesc"
                        formik={packageValidation}
                      />

                      <LimitInputItem
                        label="Çilingir"
                        limitName="locksmith"
                        descName="locksmithDesc"
                        formik={packageValidation}
                      />
                      {/* --------------------------------------- */}

                      <LimitInputItem
                        label="Heyelan / Doğal Afet"
                        limitName="landslide"
                        descName="landslideDesc"
                        formik={packageValidation}
                      />

                      <LimitInputItem
                        label="Far Arızası"
                        limitName="headlight"
                        descName="headlightDesc"
                        formik={packageValidation}
                      />

                      <LimitInputItem
                        label="Sağlık Sorunu (Sürücü)"
                        limitName="health"
                        descName="healthDesc"
                        formik={packageValidation}
                      />

                      <LimitInputItem
                        label="Yorgunluk (Tek Sürücü)"
                        limitName="tired"
                        descName="tiredDesc"
                        formik={packageValidation}
                      />

                      <LimitInputItem
                        label="Elektrikli Araç Şarj"
                        limitName="evFault"
                        descName="evFaultDesc"
                        formik={packageValidation}
                      />

                      <LimitInputItem
                        label="Yabani Hayvan Hasarı"
                        limitName="wildAnimal"
                        descName="wildAnimalDesc"
                        formik={packageValidation}
                      />
                    </Row>
                  </CardBody>
                </Card>

                <Card>
                  <CardHeader>
                    <h5 className="card-title mb-0">Sözleşme ve Detaylar</h5>
                  </CardHeader>
                  <CardBody>
                    <div className="mb-4">
                      <Label className="form-label">Paket Açıklaması</Label>
                      {packageValidation.touched.description &&
                        packageValidation.errors.description && (
                          <div className="text-danger mb-2">
                            {packageValidation.errors.description}
                          </div>
                        )}
                      <div style={{ height: "300px" }}>
                        <div
                          ref={descriptionQuillRef}
                          style={{ height: "250px" }}
                        />
                      </div>
                    </div>

                    <div className="mb-2">
                      <Label className="form-label">Satış Sözleşmesi</Label>
                      {packageValidation.touched.agreement &&
                        packageValidation.errors.agreement && (
                          <div className="text-danger mb-2">
                            {packageValidation.errors.agreement}
                          </div>
                        )}
                      <div style={{ height: "300px" }}>
                        <div
                          ref={agreementQuillRef}
                          style={{ height: "250px" }}
                        />
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Form>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PackageUpdateNew;
